import React from "react";
import PropTypes from "prop-types";
import { ArrowRight } from "lucide-react";
import SectionTitle from "./ui/SectionTitle";
import TasteTag from "./ui/TasteTag";
import BridgeText from "./BridgeText";
import { handleInternalLinkClick } from "../utils/navigation";
import { trackEvent } from "../utils/analytics";
import { bridgePreviewText } from "../utils/bridgeText";

const PairingsRail = ({
  pairings = [],
  title = "For Fans Of…",
  subtitle = "Each pick is paired with a show you might already know — and comes with a reason why.",
  minPairings = 3,
  limit = 3,
  surface = "homepage_pairings_rail",
}) => {
  const validPairings = Array.isArray(pairings) ? pairings.slice(0, limit) : [];
  const isTwoCardLayout = validPairings.length === 2;
  if (validPairings.length < minPairings) {
    return null;
  }

  return (
    <section className="taste-recommendation-band space-y-4" aria-labelledby="pairings-rail-heading">
      <header className="space-y-1">
        <SectionTitle variant="h2" id="pairings-rail-heading">
          {title}
        </SectionTitle>
        <p className="text-body-small text-muted-foreground">
          {subtitle}
        </p>
      </header>

      <div className={validPairings.length === 2
        ? "-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-4 lg:mx-0 lg:grid lg:grid-cols-2 lg:overflow-visible lg:px-0 lg:pb-0"
        : "-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-4 lg:mx-0 lg:grid lg:grid-cols-3 lg:overflow-visible lg:px-0 lg:pb-0"
      }>
        {validPairings.map((pairing) => {
          const anchorHref = `/show/${pairing.anchor.slug}`;
          const currentHref = `/show/${pairing.current.slug}`;
          const sharedTasteTags = Array.isArray(pairing.sharedTasteTags)
            ? pairing.sharedTasteTags.slice(0, 3)
            : [];
          const bridgePreview = bridgePreviewText(pairing.bridge, pairing.anchor.title, pairing.current.title);
          const trackPairingClick = (target) => {
            trackEvent("recommendation_click", {
              surface,
              target,
              anchor_slug: pairing.anchor.slug,
              current_slug: pairing.current.slug,
            });
          };

          return (
            <article
              key={`${pairing.anchor.slug}-${pairing.current.slug}`}
              className={isTwoCardLayout
                ? "flex min-w-[85vw] snap-center flex-col gap-4 rounded-lg border border-accent-gold/45 bg-card/80 p-4 shadow-card sm:min-w-[40rem] lg:min-w-0"
                : "flex min-w-[85vw] snap-center flex-col gap-4 rounded-lg border border-accent-gold/45 bg-card/80 p-5 shadow-card lg:min-w-0"
              }
            >
              <div className={isTwoCardLayout ? "grid gap-4 sm:grid-cols-[18.5rem_minmax(0,1fr)] sm:items-start" : "space-y-4"}>
                <div className={isTwoCardLayout ? "flex items-center gap-3 sm:gap-2" : "flex items-center gap-3"}>
                  {pairing.anchor.poster && (
                    <a
                      href={anchorHref}
                      onClick={(event) => {
                        trackPairingClick("anchor");
                        handleInternalLinkClick(event, anchorHref);
                      }}
                      aria-label={`View ${pairing.anchor.title}`}
                      className={isTwoCardLayout
                        ? "block h-40 w-28 shrink-0 overflow-hidden rounded-md border border-border bg-poster-placeholder-gradient shadow-card transition-transform hover:-translate-y-0.5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring sm:h-52 sm:w-[8rem]"
                        : "block h-20 w-14 shrink-0 overflow-hidden rounded-md bg-poster-placeholder-gradient transition-transform hover:-translate-y-0.5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                      }
                    >
                      <img
                        src={pairing.anchor.poster}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover grayscale-[20%]"
                      />
                    </a>
                  )}
                  <ArrowRight className="h-4 w-4 shrink-0 text-secondary-readable" aria-hidden="true" />
                  {pairing.current.poster && (
                    <a
                      href={currentHref}
                      onClick={(event) => {
                        trackPairingClick("current");
                        handleInternalLinkClick(event, currentHref);
                      }}
                      aria-label={`View ${pairing.current.title}`}
                      className={isTwoCardLayout
                        ? "block h-40 w-28 shrink-0 overflow-hidden rounded-md border border-accent-gold/70 bg-poster-placeholder-gradient shadow-card transition-transform hover:-translate-y-0.5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring sm:h-52 sm:w-[8rem]"
                        : "block h-20 w-14 shrink-0 overflow-hidden rounded-md bg-poster-placeholder-gradient transition-transform hover:-translate-y-0.5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                      }
                    >
                      <img
                        src={pairing.current.poster}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </a>
                  )}
                </div>

                <div className="min-w-0 space-y-3">
                  <p className="text-body-small font-semibold text-muted-foreground">
                    For fans of{" "}
                    <a
                      href={anchorHref}
                      onClick={(event) => {
                        trackPairingClick("anchor");
                        handleInternalLinkClick(event, anchorHref);
                      }}
                      className="title-link font-semibold text-foreground"
                    >
                      {pairing.anchor.title}
                    </a>
                  </p>

                  <h3 className="text-h4 font-semibold leading-tight text-foreground">
                    <a
                      href={currentHref}
                      onClick={(event) => {
                        trackPairingClick("current");
                        handleInternalLinkClick(event, currentHref);
                      }}
                      className="title-link"
                    >
                      {pairing.current.title}
                    </a>
                  </h3>

                  {bridgePreview && (
                    <p className="text-body-small leading-relaxed text-foreground/80">
                      <BridgeText text={bridgePreview} titles={[pairing.anchor.title, pairing.current.title]} />
                    </p>
                  )}
                </div>
              </div>

              {sharedTasteTags.length > 0 && (
                <ul
                  className="mt-auto flex flex-wrap gap-2 border-t border-border/60 pt-3"
                  aria-label={`Tags shared by ${pairing.anchor.title} and ${pairing.current.title}`}
                >
                  {sharedTasteTags.map((tag) => (
                    <li key={tag}>
                      <TasteTag>
                        {tag}
                      </TasteTag>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          );
        })}
      </div>

      <p className="text-body-small">
        <a
          href="/most-recommended"
          onClick={(event) => {
            trackEvent("recommendation_graph_click", { surface });
            handleInternalLinkClick(event, "/most-recommended");
          }}
          className="font-medium text-secondary-readable underline-offset-2 hover:underline"
        >
          Start from a show you love →
        </a>
      </p>
    </section>
  );
};

PairingsRail.propTypes = {
  pairings: PropTypes.arrayOf(
    PropTypes.shape({
      anchor: PropTypes.shape({
        slug: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        poster: PropTypes.string,
      }).isRequired,
      current: PropTypes.shape({
        slug: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        poster: PropTypes.string,
      }).isRequired,
      sharedTasteTags: PropTypes.arrayOf(PropTypes.string),
      bridge: PropTypes.string,
    })
  ),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  minPairings: PropTypes.number,
  limit: PropTypes.number,
  surface: PropTypes.string,
};

export default PairingsRail;
